// web/src/screens/MySite.tsx — the customer's site(s). Lists sites; ?id= drills into one (stage badge, live address,
// requests, releases) + "Ask for a change" (live sites only, portal spec 14) / "Send photos" CTAs (D07, D06 A3/A4).
// The go-live checklist lives on Home (spec 14); the old self-serve go-live card is gone (the go-live brain does it).
// Hidden with builds (portal spec 10): "Build a new website", Claim a spec site, Delete a site, Final checks.
import { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import type { SiteStage } from '../lib/types';
import { PageHeader } from '../components/PageHeader';
import { AsyncBoundary } from '../components/AsyncBoundary';
import { Skeleton } from '../components/Skeleton';
import { SiteCard } from '../components/SiteCard';
import { RequestCard } from '../components/RequestCard';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { Icon } from '../components/Icon';
import { toast } from '../components/Toast';
import { FEATURES } from '../lib/features';

const BUILD_LABEL: Record<string, string> = {
  intake: 'Starting', interviewing: 'Interview', awaiting_approval: 'Awaiting approval',
  paid: 'Queued', queued: 'Queued', researching: 'Researching', planning: 'Designing',
  building: 'Building', deploying: 'Deploying', live: 'Live', failed: 'Failed',
};

// Badge per registry stage (portal spec 11). SiteCard uses the same labels.
const STAGE_BADGE: Record<SiteStage, { label: string; cls: string }> = {
  demo: { label: 'Your preview', cls: 'text-info' },
  sold: { label: 'Getting ready', cls: 'text-warn' },
  finishing: { label: 'Getting ready', cls: 'text-warn' },
  going_live: { label: 'Going live', cls: 'text-info' },
  live: { label: 'Live', cls: 'text-run' },
  paused: { label: 'Paused', cls: 'text-danger' },
  offboarding: { label: 'Ending', cls: 'text-warn' },
  offboarded: { label: 'Ended', cls: 'text-muted' },
  archived: { label: 'Archived', cls: 'text-muted' },
  legacy: { label: 'Live', cls: 'text-run' },
};

export function MySite() {
  const [params] = useSearchParams();
  const id = params.get('id');
  return id ? <OneSite id={id} /> : <AllSites />;
}

function AllSites() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['sites'], queryFn: () => api.listSites() });

  const claim = useMutation({
    mutationFn: (id: string) => api.claimSite(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sites'] });
      toast({ kind: 'success', message: 'Site claimed — it is now active.' });
    },
    onError: (e: unknown) => toast({ kind: 'error', message: e instanceof Error ? e.message : 'Could not claim site' }),
  });

  // In-progress builds, so a status screen you navigated away from is always one click away.
  // Polls while anything is mid-build; stops once everything is live/failed.
  const builds = useQuery({
    queryKey: ['builds'],
    queryFn: () => api.listBuilds(),
    enabled: FEATURES.builds, // the builds API isn't mounted while builds are hidden (portal spec 10)
    refetchInterval: (query) =>
      (query.state.data ?? []).some((b) => b.status !== 'live' && b.status !== 'failed') ? 4000 : false,
  });
  const inProgress = (builds.data ?? []).filter((b) => b.status !== 'live');

  return (
    <>
      <PageHeader title="My Site" subtitle="Your websites. Open one to see its requests and releases."
        action={FEATURES.builds ? (
          <button onClick={() => nav('/build')}
            className="inline-flex min-h-11 items-center gap-2 rounded-md bg-run px-4 font-medium text-runink transition-transform active:scale-[0.98]">
            <Icon name="plus" size={16} />Build a new website
          </button>
        ) : undefined} />

      {FEATURES.builds && inProgress.length > 0 && (
        <section className="mb-5">
          <h2 className="mb-2 font-head text-sm text-muted">Builds in progress</h2>
          <div className="flex flex-col gap-2">
            {inProgress.map((b) => (
              <button key={b.id} onClick={() => nav(`/build?id=${b.id}`)}
                className="flex items-center justify-between gap-3 rounded-lg border border-hair bg-surface p-3 text-left transition-transform hover:border-surface2 active:scale-[0.99]">
                <span className="flex items-center gap-2 min-w-0">
                  <Icon name={b.status === 'failed' ? 'alert' : 'loader'} size={16}
                    className={b.status === 'failed' ? 'text-danger' : 'text-info'} />
                  <span className="truncate text-ink">{b.businessName ?? 'New website'}</span>
                </span>
                <span className="flex shrink-0 items-center gap-2 text-xs">
                  <span className={b.status === 'failed' ? 'text-danger' : 'text-info'}>{BUILD_LABEL[b.status] ?? b.status}</span>
                  <span className="inline-flex items-center gap-1 text-muted">View status<Icon name="chevron-right" size={14} /></span>
                </span>
              </button>
            ))}
          </div>
        </section>
      )}

      <AsyncBoundary query={q} skeleton={<Skeleton variant="card" count={3} />}
        isEmpty={(s) => s.length === 0}
        empty={FEATURES.builds
          ? { icon: 'world', title: 'No sites yet', body: 'Build your first website to get started.' }
          : { icon: 'world', title: 'No sites yet', body: 'Your site will appear here. We add it as soon as your account is set up.' }}>
        {(sites) => (
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {sites.map((s) => (
              <SiteCard key={s.id} site={s}
                onClaim={FEATURES.builds ? (id) => claim.mutate(id) : undefined}
                claiming={claim.isPending && claim.variables === s.id} />
            ))}
          </div>
        )}
      </AsyncBoundary>
    </>
  );
}

function OneSite({ id }: { id: string }) {
  const nav = useNavigate();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['site', id], queryFn: () => api.getSite(id) });
  const [confirmDelete, setConfirmDelete] = useState(false);

  const del = useMutation({
    mutationFn: () => api.deleteSite(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sites'] });
      toast({ kind: 'success', message: 'Site deleted.' });
      nav('/site');
    },
    onError: (e: unknown) => {
      setConfirmDelete(false);
      toast({ kind: 'error', message: e instanceof Error ? e.message : 'Could not delete site' });
    },
  });

  const claim = useMutation({
    mutationFn: () => api.claimSite(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['site', id] });
      qc.invalidateQueries({ queryKey: ['sites'] });
      toast({ kind: 'success', message: 'Site claimed — it is now active.' });
    },
    onError: (e: unknown) => toast({ kind: 'error', message: e instanceof Error ? e.message : 'Could not claim site' }),
  });

  return (
    <AsyncBoundary query={q} skeleton={<Skeleton variant="card" count={3} />}>
      {(site) => {
        const badge = STAGE_BADGE[site.stage] ?? STAGE_BADGE.legacy;
        return (
        <>
          <button onClick={() => nav('/site')} className="mb-4 inline-flex items-center gap-1 text-sm text-muted hover:text-ink"><Icon name="arrow-left" size={16} />All sites</button>
          <PageHeader title={site.name}
            subtitle={site.liveHost ?? (site.url ? site.url.replace(/^https?:\/\//, '') : 'No domain connected yet')} />
          {/* Actions get their own wrapping row (not the header slot), so three buttons never push past 390 px. */}
              <div className="-mt-2 mb-5 flex flex-wrap gap-2">
                {FEATURES.builds && site.state === 'spec' && (
                  <button onClick={() => claim.mutate()} disabled={claim.isPending}
                    className="inline-flex min-h-11 items-center gap-2 rounded-md bg-run px-4 font-medium text-runink transition-transform active:scale-[0.98] disabled:opacity-50">
                    <Icon name="check" size={16} />{claim.isPending ? 'Working…' : 'Claim'}
                  </button>
                )}
                {/* Changes open up once the site is live (portal spec 14); the API answers 409 before that. */}
                {(site.stage === 'live' || site.stage === 'legacy') && (
                  <button onClick={() => nav(`/request?site=${site.id}`)}
                    className="inline-flex min-h-11 items-center gap-2 rounded-md bg-run px-4 font-medium text-runink transition-transform active:scale-[0.98]">
                    <Icon name="plus" size={16} />Ask for a change
                  </button>
                )}
                <button onClick={() => nav(`/photo?site=${site.id}`)}
                  className="inline-flex min-h-11 items-center gap-2 rounded-md border border-hair px-4 text-sm text-ink">
                  <Icon name="image" size={16} />Send photos
                </button>
                {site.url && !/yourproject\.ai$/.test(site.url.replace(/^https?:\/\//, '').replace(/\/.*$/, '')) && (
                  <button onClick={() => nav(`/dns?site=${site.id}`)}
                    className="inline-flex min-h-11 items-center gap-2 rounded-md border border-hair px-4 text-sm text-ink">
                    <Icon name="globe" size={16} />Domains & DNS
                  </button>
                )}
                {FEATURES.builds && (
                  <button onClick={() => setConfirmDelete(true)}
                    className="inline-flex min-h-11 items-center gap-2 rounded-md border border-hair px-4 text-sm text-danger">
                    <Icon name="x" size={16} />Delete
                  </button>
                )}
              </div>

          <div className="grid grid-cols-3 gap-3">
            <Stat label="State" value={badge.label} accent />
            <Stat label="Open requests" value={site.openRequests} />
            <Stat label="Latest release" value={site.lastRelease?.version ?? '—'} accent />
          </div>
          {/* No repo line for clients (00 §3.8: plain words, no "repo"). */}

          {/* Pre-live: the go-live checklist is on Home (portal spec 14). */}
          {(['sold', 'finishing'] as SiteStage[]).includes(site.stage) && (
            <div className="mt-5 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-hair bg-surface p-4 text-sm">
              <span className="text-muted">What we need from you before launch is on Home.</span>
              <button onClick={() => nav('/')} className="inline-flex min-h-11 items-center gap-2 rounded-md border border-hair px-4 text-ink"><Icon name="home" size={15} />Open Home</button>
            </div>
          )}
          {site.stage === 'going_live' && (
            <p className="mt-5 rounded-lg border border-hair bg-surface p-4 text-sm text-muted">We’re putting your site live. Nothing for you to do.</p>
          )}

          {/* Final checks (CHALICE §5 C4): on-demand deep check of the live site. Previewing an
              update IS the primary QA; this button is the belt-and-braces sweep. */}
          {FEATURES.builds && <FinalChecksCard siteId={site.id} gate={site.gate} />}

          <section className="mt-7">
            <h2 className="mb-3 font-head text-sm text-muted">Requests</h2>
            {site.requests.length
              ? <div className="flex flex-col gap-3">{site.requests.map((r) => <RequestCard key={r.id} run={r} />)}</div>
              : <p className="rounded-lg border border-hair bg-surface/40 px-4 py-6 text-center text-sm text-muted">No requests for this site yet.</p>}
          </section>

          {site.releases.length > 0 && (
            <section className="mt-7">
              <h2 className="mb-3 font-head text-sm text-muted">Releases</h2>
              <div className="flex flex-col gap-2">
                {site.releases.map((rel) => (
                  <div key={rel.id} className="flex items-center justify-between rounded-lg border border-hair bg-surface p-3">
                    <span className="flex items-center gap-2 font-head text-sm text-run"><Icon name="rocket" size={15} />{rel.version}</span>
                    {rel.url && <a href={rel.url} className="inline-flex items-center gap-1 text-xs text-info" target="_blank" rel="noreferrer">View <Icon name="external" size={13} /></a>}
                  </div>
                ))}
              </div>
            </section>
          )}

          <ConfirmDialog open={confirmDelete} title="Delete this site?"
            body="This cancels its billing immediately, releases its hosting, and removes it for good. This cannot be undone, and nothing is refunded."
            confirmLabel={del.isPending ? 'Deleting…' : 'Delete site'} destructive
            onConfirm={() => del.mutate()} onCancel={() => setConfirmDelete(false)} />
        </>
        );
      }}
    </AsyncBoundary>
  );
}

function Stat({ label, value, accent }: { label: string; value: string | number; accent?: boolean }) {
  return (
    <div className="rounded-lg border border-hair bg-surface p-3">
      <div className="text-xs text-muted">{label}</div>
      <div className={`mt-1 truncate font-head text-sm ${accent ? 'text-run' : 'text-ink'}`}>{value}</div>
    </div>
  );
}

// Final checks (CHALICE §5 C4): the on-demand deep check for a live site — rides the existing
// qaOnly path; the report lands advisory-only and shows here. Copy mirrors Build.tsx's gate card.
function FinalChecksCard({ siteId, gate }: { siteId: string; gate?: import('../lib/types').BuildGateSummary }) {
  const qc = useQueryClient();
  const [pending, setPending] = useState(false);
  async function run() {
    setPending(true);
    try {
      await api.siteQa(siteId);
      toast({ kind: 'success', message: 'Checks started — results appear here in a few minutes.' });
      // poll the site a few times so the fresh report shows without a manual refresh
      setTimeout(() => qc.invalidateQueries({ queryKey: ['site', siteId] }), 15_000);
      setTimeout(() => qc.invalidateQueries({ queryKey: ['site', siteId] }), 60_000);
    } catch {
      toast({ kind: 'error', message: "Couldn't start checks for this site." });
    } finally {
      setPending(false);
    }
  }
  return (
    <section className="mt-7 rounded-lg border border-hair bg-surface p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-head text-sm text-muted">Final checks</h2>
          <p className="mt-1 text-xs text-muted">A deep sweep of your live site — phones, links, images{gate?.checkoutTested !== undefined ? ', checkout' : ''}.</p>
        </div>
        <button onClick={run} disabled={pending}
          className="inline-flex min-h-11 items-center gap-2 rounded-md border border-hair px-4 text-sm text-ink disabled:opacity-50">
          <Icon name="flask" size={15} />{pending ? 'Starting…' : gate ? 'Re-run checks' : 'Run final checks'}
        </button>
      </div>
      {gate && (
        <div className="mt-3 flex flex-col gap-1 text-sm">
          <GateLine ok={gate.mobileFriendly} label="Looks great on phones" />
          <GateLine ok={gate.linksWork} label="Every link works" />
          <GateLine ok={gate.imagesReal} label="Real images, no blanks" />
          {gate.checkoutTested !== undefined && <GateLine ok={gate.checkoutTested} label="Checkout tested" />}
          {gate.allClear && <p className="mt-1 text-xs text-run">All checks passed.</p>}
        </div>
      )}
    </section>
  );
}

function GateLine({ ok, label }: { ok: boolean; label: string }) {
  return (
    <span className={`inline-flex items-center gap-2 ${ok ? 'text-ink' : 'text-warn'}`}>
      <Icon name={ok ? 'check' : 'alert'} size={14} />{label}
    </span>
  );
}
